import { Record, List } from "immutable";
import { IImageData } from "common/records/Image";
import { addressFromJSON, AddressRecord, IAddressData } from "common/records/Address";
import { checkDate } from "common/helpers/Date";
import { Nullable } from "common/types";
import { IOrgMetadata, orgMetadataFromJSON, OrgMetadataRecord } from "common/records/OrgMetadata";
import { DateStringNumber } from "common/records";

export const orgOverviewFromJSON = (json: Partial<IOrgOverviewData>): OrgOverviewRecord => {
  const record: IOrgOverviewRecord = { ...(json as any) };

  if (json.lead_sources) {
    record.lead_sources = List<string>(json.lead_sources);
  } else {
    record.lead_sources = List<string>();
  }

  if (json.events) {
    record.events = List(json.events);
  }

  if (json.address) {
    record.address = addressFromJSON(json.address);
  }

  if (json.activated_price_list) {
    record.activated_price_list_id = json.activated_price_list.id;
  }

  if (json.activated_inspection_list) {
    record.activated_inspection_list_id = json.activated_inspection_list.id;
  }

  if (json.emails) {
    record.emails = List(json.emails);
  }

  if (json.created_at) {
    record.created_at = checkDate(json.created_at);
  }

  if (json.updated_at) {
    record.updated_at = checkDate(json.updated_at);
  }

  if (json.images?.length) {
    record.image_ids = List(json.images.map((image) => image.id));
  }

  if (json.logo) {
    record.logo_id = json.logo.id;
  }
  if (json.default_cover_image) {
    record.default_cover_image_id = json.default_cover_image.id;
  }
  if (json.custom_signature_email_image) {
    record.custom_signature_email_image_id = json.custom_signature_email_image.id;
  }

  return new OrgOverviewRecord(record);
};

export const getPrefConfig = (org: OrgOverviewRecord, pref: string[]): any => {
  return org.getIn(["preferences", "config"].concat(pref));
};

export const orgNameAddress = (org: OrgOverviewRecord): string => {
  const address = org.address;
  let cityState = "";
  if (address.city && address.state) {
    cityState = ` (${address.city}, ${address.state})`;
  } else if (address.city) {
    cityState = ` (${address.city})`;
  } else if (address.state) {
    cityState = ` (${address.state})`;
  }
  return `${org.name}${cityState}`;
};

export interface IOrgOverviewData {
  id: number;
  uuid: string;
  parent_id: number;
  name: string;
  ancestral_name: string;
  phone_number: string;
  website: string;
  created_at: DateStringNumber;
  updated_at: DateStringNumber;
  status: string;
  metadata: IOrgMetadata;
  "prevent_login?": boolean;
  base36_id: string;
  job_count: number;
  org_count: number;
  user_count: number;
  descendants_count: number;
  presentation_count: number;
  url: string;
  address: IAddressData;
  images: IImageData[];
  logo: Nullable<IImageData>;
  default_cover_image: Nullable<IImageData>;
  custom_signature_email_image: Nullable<IImageData>;
  events: string[];
  lead_sources: Array<string>;
  activated_price_list: any;
  activated_inspection_list: any;
  emails: any[];
}

export interface IOrgOverviewRecord {
  id: number;
  uuid: string;
  parent_id: number;
  name: string;
  ancestral_name: string;
  phone_number: string;
  website: string;
  created_at: Date;
  updated_at: Date;
  status: string;
  metadata: OrgMetadataRecord;
  "prevent_login?": boolean;
  base36_id: string;
  job_count: number;
  org_count: number;
  user_count: number;
  descendants_count: number;
  presentation_count: number;
  url: string;
  address: AddressRecord;
  image_ids: List<number>;
  logo_id: Nullable<number>;
  default_cover_image_id: Nullable<number>;
  custom_signature_email_image_id: Nullable<number>;
  events: List<string>;
  lead_sources: List<string>;
  activated_price_list_id: number;
  activated_inspection_list_id: number;
  emails: List<any>;
  loading: boolean;
}

export const defaultOrgOverviewProps = {
  id: 0,
  uuid: "",
  parent_id: 0,
  name: "",
  ancestral_name: "",
  phone_number: "",
  website: "",
  created_at: new Date(),
  updated_at: new Date(),
  status: "created",
  metadata: orgMetadataFromJSON({}),
  "prevent_login?": false,
  base36_id: "",
  job_count: 0,
  org_count: 0,
  user_count: 0,
  descendants_count: 0,
  presentation_count: 0,
  url: "",
  address: addressFromJSON({}),
  loading: false,
  image_ids: List<number>(),
  logo_id: null as Nullable<number>,
  default_cover_image_id: null as Nullable<number>,
  custom_signature_email_image_id: null as Nullable<number>,
  events: List<string>(),
  lead_sources: List<string>(),
  activated_price_list_id: 0,
  activated_inspection_list_id: 0,
  emails: List<any>(),
};

export class OrgOverviewRecord extends Record(defaultOrgOverviewProps) implements IOrgOverviewRecord {}
